import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api, { API } from "../api";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import TaskDetailDialog, { checklistStats, fmtHours } from "../components/TaskDetailDialog";
import { Progress } from "../components/ui/progress";
import TaskTemplatesDialog, { SaveTemplateDialog } from "../components/TaskTemplatesDialog";
import ClientTasksDialog from "../components/ClientTasksDialog";
import { Plus, Trash2, Calendar, Pencil, SquareKanban, MessageSquare, Paperclip, ListChecks, Repeat, ReceiptText, LayoutTemplate, BookmarkPlus, FolderOpen, Building2, Play, Square } from "lucide-react";

const COLUMNS = ["todo", "doing", "review", "done"];
const PRIORITY_CLS = {
  high: "bg-red-500/15 text-red-500 border-red-500/30",
  medium: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  low: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};

const EMPTY_TASK = { title: "", description: "", assignee_ids: [], column: "todo", priority: "medium", due_date: "", entity_id: "", board_id: "", recurrence: "none", contact_id: "", contact_name: "", amount: "", billable: false, hourly_rate: "" };
const initials = (u) => (u.name || u.email || "?").slice(0, 2).toUpperCase();

function AssigneeAvatars({ ids, users }) {
  const list = ids.map((id) => users.find((u) => u.user_id === id)).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="flex items-center gap-1.5" data-testid="task-assignees">
      <div className="flex -space-x-2">
        {list.map((u) => (
          <Avatar key={u.user_id} className="h-6 w-6 border border-background" title={u.name || u.email}>
            <AvatarImage src={u.picture} />
            <AvatarFallback className="text-[10px]">{initials(u)}</AvatarFallback>
          </Avatar>
        ))}
      </div>
      <span className="text-xs text-muted-foreground truncate max-w-[120px]">{list.map((u) => u.name || u.email).join(", ")}</span>
    </div>
  );
}

function TaskDialog({ task, users, entities, boardId, onClose, onSaved }) {
  const { t, entity } = useApp();
  const [form, setForm] = useState(task ? { ...EMPTY_TASK, ...task, assignee_ids: task.assignee_ids || [], due_date: task.due_date || "", entity_id: task.entity_id || "", board_id: task.board_id || "", recurrence: task.recurrence || "none", contact_id: task.contact_id || "", contact_name: task.contact_name || "", amount: task.amount || "", hourly_rate: task.hourly_rate || "" } : { ...EMPTY_TASK, entity_id: entity || "" });
  const [contacts, setContacts] = useState([]);
  const isEdit = !!task;
  useEffect(() => {
    if (!form.entity_id) return;
    api.get(`/contacts?entity_id=${form.entity_id}`).then((r) => setContacts(r.data)).catch(() => setContacts([]));
  }, [form.entity_id]);

  const toggle = (id) => setForm((f) => ({ ...f, assignee_ids: f.assignee_ids.includes(id) ? f.assignee_ids.filter((x) => x !== id) : [...f.assignee_ids, id] }));

  const save = async () => {
    if (!form.title.trim()) return;
    const c = contacts.find((x) => x.id === form.contact_id);
    const payload = { ...form, assignee_id: null, due_date: form.due_date || null, entity_id: form.entity_id || null, board_id: boardId || form.board_id || null, recurrence: form.recurrence === "none" ? null : form.recurrence,
      contact_id: form.contact_id || null, contact_name: c ? (c.ragione_sociale || c.name) : (form.contact_name || null), amount: Number(form.amount) || 0, billable: !!form.billable, hourly_rate: Number(form.hourly_rate) || 0 };
    try {
      if (isEdit) await api.put(`/tasks/${task.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(t("msg.saved"));
      onSaved();
      onClose();
    } catch { toast.error(t("msg.error")); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="task-dialog">
        <DialogHeader><DialogTitle>{isEdit ? t("task.edit") : t("task.new")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>{t("task.title")}</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
          <div><Label>{t("common.description")}</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-description-input" /></div>
          <div>
            <Label>{t("task.assignees")}</Label>
            <div className="mt-1 rounded-md border border-border divide-y divide-border max-h-44 overflow-y-auto" data-testid="task-assignee-list">
              {users.length === 0 && <div className="p-3 text-xs text-muted-foreground">{t("task.noUsers")}</div>}
              {users.map((u) => (
                <label key={u.user_id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                  <Checkbox checked={form.assignee_ids.includes(u.user_id)} onCheckedChange={() => toggle(u.user_id)} data-testid={`task-assignee-${u.user_id}`} />
                  <Avatar className="h-6 w-6"><AvatarImage src={u.picture} /><AvatarFallback className="text-[10px]">{initials(u)}</AvatarFallback></Avatar>
                  <span className="flex-1 truncate">{u.name || u.email}</span>
                  <span className="text-[10px] uppercase text-muted-foreground">{t(`users.${u.role}`)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("task.priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t("task.low")}</SelectItem>
                  <SelectItem value="medium">{t("task.medium")}</SelectItem>
                  <SelectItem value="high">{t("task.high")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("common.dueDate")}</Label>
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("task.entity")}</Label>
              <Select value={form.entity_id} onValueChange={(v) => setForm({ ...form, entity_id: v })}>
                <SelectTrigger data-testid="task-entity-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {entities.map((e) => <SelectItem key={e.entity_id} value={e.entity_id}>{e.code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("task.recurrence")}</Label>
              <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
                <SelectTrigger data-testid="task-recurrence-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("task.recNone")}</SelectItem>
                  <SelectItem value="weekly">{t("task.recWeekly")}</SelectItem>
                  <SelectItem value="monthly">{t("task.recMonthly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>{t("task.client")}</Label>
              <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="task-client-select"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("task.noClient")}</SelectItem>
                  {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("task.amount")}</Label>
              <div className="flex items-center gap-2">
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="task-amount-input" />
                <label className="flex items-center gap-1.5 text-xs whitespace-nowrap cursor-pointer"><Checkbox checked={!!form.billable} onCheckedChange={(v) => setForm({ ...form, billable: !!v })} data-testid="task-billable-checkbox" />{t("task.billable")}</label>
              </div>
            </div>
          </div>
          <div><Label>{t("task.hourlyRate")}</Label>
            <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} placeholder="0 = usa importo fisso" data-testid="task-rate-input" /></div>
          <Button className="w-full" onClick={save} data-testid="task-submit-button">{t("common.save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Tasks() {
  const { t, entities, entity, user } = useApp();
  const { boardId } = useParams();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [boards, setBoards] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [onlyMine, setOnlyMine] = useState(false);
  const [personFilter, setPersonFilter] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);
  const [saveTpl, setSaveTpl] = useState(null);
  const [byClient, setByClient] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [commentTask, setCommentTask] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const isViewer = user?.role === "viewer";

  const moveTo = async (task, column) => {
    if (!task || task.column === column) return;
    setTasks((ts) => ts.map((x) => (x.id === task.id ? { ...x, column } : x)));
    try {
      const r = await api.patch(`/tasks/${task.id}/move`, { column });
      if (r.data?.spawned) { toast.success(t("task.recSpawned")); load(); }
    } catch { toast.error(t("msg.error")); load(); }
  };
  const onDrop = (e, col) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/task-id");
    moveTo(tasks.find((x) => x.id === id), col);
  };

  const load = useCallback(() => {
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/tasks/assignable-users").then((r) => setUsers(r.data)).catch(() => {});
    if (boardId) api.get("/boards").then((r) => setBoards(r.data)).catch(() => {});
  }, [boardId]);
  useEffect(() => { load(); }, [load]);

  const board = boards.find((b) => b.id === boardId);
  const visibleTasks = tasks
    .filter((x) => (boardId ? x.board_id === boardId : true))
    .filter((x) => (onlyMine ? (x.assignee_ids || []).includes(user?.user_id) : true))
    .filter((x) => (personFilter ? (x.assignee_ids || []).includes(personFilter) : true));

  const move = async (task, dir) => {
    const idx = COLUMNS.indexOf(task.column);
    const next = COLUMNS[idx + dir];
    if (!next) return;
    moveTo(task, next);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/tasks/${id}`);
      toast.success(t("msg.deleted"));
      load();
    } catch { toast.error(t("msg.error")); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6 relative rounded-xl" data-testid="tasks-page"
      style={board?.bg_path ? {
        backgroundImage: `linear-gradient(rgba(5,8,15,0.72), rgba(5,8,15,0.72)), url(${API}/files/download?path=${encodeURIComponent(board.bg_path)})`,
        backgroundSize: "cover", backgroundPosition: "center", padding: "1.25rem", minHeight: "70vh",
      } : {}}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <SquareKanban className="h-7 w-7 text-primary" />
          {boardId ? (
            <>
              <Link to="/boards" className="text-muted-foreground hover:text-foreground transition-colors text-lg">{t("nav.boards")}</Link>
              <span className="text-muted-foreground">/</span>
              <span data-testid="board-title">{board?.name || "..."}</span>
            </>
          ) : isViewer ? t("task.assignedToYou") : t("nav.task")}
        </h1>
        <div className="flex gap-2 items-center">
          {!isViewer && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer mr-2">
              <Checkbox checked={onlyMine} onCheckedChange={(v) => setOnlyMine(!!v)} data-testid="only-mine-toggle" />
              {t("task.onlyMine")}
            </label>
          )}
          {!isViewer && (
            <Button variant="outline" onClick={() => setByClient(true)} data-testid="by-client-button">
              <Building2 className="h-4 w-4 mr-2" />{t("task.byClient")}
            </Button>
          )}
          {!isViewer && (
            <Button variant="outline" onClick={() => setTplOpen(true)} data-testid="task-templates-button">
              <LayoutTemplate className="h-4 w-4 mr-2" />{t("tpl.title")}
            </Button>
          )}
          {!isViewer && (
            <Button onClick={() => setNewOpen(true)} data-testid="add-task-button">
              <Plus className="h-4 w-4 mr-2" />{t("task.new")}
            </Button>
          )}
        </div>
      </div>

      {!isViewer && users.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="person-filter-bar">
          <button type="button" onClick={() => setPersonFilter(null)} data-testid="person-filter-all"
            className={`h-8 px-3 rounded-full text-xs border transition-colors ${!personFilter ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>
            {t("task.everyone")}
          </button>
          {users.map((u) => {
            const n = tasks.filter((x) => (boardId ? x.board_id === boardId : true) && (x.assignee_ids || []).includes(u.user_id) && x.column !== "done").length;
            const active = personFilter === u.user_id;
            return (
              <button key={u.user_id} type="button" onClick={() => setPersonFilter(active ? null : u.user_id)} title={u.name || u.email} data-testid={`person-filter-${u.user_id}`}
                className={`flex items-center gap-1.5 h-8 pl-1 pr-3 rounded-full border text-xs transition-all ${active ? "bg-primary/15 border-primary text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>
                <Avatar className="h-6 w-6"><AvatarImage src={u.picture} /><AvatarFallback className="text-[10px]">{initials(u)}</AvatarFallback></Avatar>
                <span>{(u.name || u.email).split(" ")[0]}</span>
                {n > 0 && <span className={`font-mono text-[10px] rounded-full px-1.5 ${active ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{n}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
        {COLUMNS.map((col) => (
          <div key={col} className={`bg-card border rounded-lg p-3 min-h-[200px] transition-colors ${dragOver === col ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`column-${col}`}
            onDragOver={(e) => { e.preventDefault(); if (dragOver !== col) setDragOver(col); }}
            onDragLeave={() => setDragOver((d) => (d === col ? null : d))}
            onDrop={(e) => onDrop(e, col)}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(`task.col.${col}`)}</span>
              <Badge variant="secondary" className="text-[10px]">{visibleTasks.filter((x) => x.column === col).length}</Badge>
            </div>
            <div className="space-y-2">
              {visibleTasks.filter((x) => x.column === col).map((task) => {
                const overdue = task.due_date && task.due_date < today && task.column !== "done";
                return (
                  <div key={task.id} draggable onDragStart={(e) => { e.dataTransfer.setData("text/task-id", task.id); e.dataTransfer.effectAllowed = "move"; }}
                    className="bg-background border border-border rounded-md p-3 space-y-2 hover:border-primary/40 transition-colors cursor-grab active:cursor-grabbing" data-testid={`task-card-${task.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium leading-snug">{task.title}</div>
                      <div className="flex items-center gap-1 shrink-0">
                        {task.recurrence && <Badge variant="outline" className="text-[10px] gap-0.5" title={t(`task.rec${task.recurrence === "weekly" ? "Weekly" : "Monthly"}`)} data-testid={`task-recurrence-${task.id}`}><Repeat className="h-2.5 w-2.5" /></Badge>}
                        {task.invoice_id && <Link to={task.invoice_kind === "purchase" ? "/purchases" : "/invoices"} data-testid={`task-invoice-${task.id}`}><Badge variant="outline" className="text-[10px] gap-0.5 hover:bg-accent"><ReceiptText className="h-2.5 w-2.5" />{task.invoice_number}</Badge></Link>}
                        {task.vault_id && <Link to="/vault" data-testid={`task-vault-${task.id}`}><Badge variant="outline" className="text-[10px] gap-0.5 hover:bg-accent text-amber-500"><FolderOpen className="h-2.5 w-2.5" />Vault</Badge></Link>}
                        {task.contact_id && <Badge variant="outline" className="text-[10px] gap-0.5 max-w-[110px] truncate" title={task.contact_name} data-testid={`task-client-${task.id}`}><Building2 className="h-2.5 w-2.5 shrink-0" />{task.contact_name}</Badge>}
                        {task.amount > 0 && <Badge variant="outline" className={`text-[10px] font-mono ${task.billed_invoice_id ? "text-emerald-500" : task.billable ? "text-amber-500" : ""}`} title={task.billed_invoice_id ? t("task.billed") : task.billable ? t("task.billable") : ""} data-testid={`task-amount-${task.id}`}>{Number(task.amount).toFixed(2)} €{task.billed_invoice_id ? " ✓" : ""}</Badge>}
                        <Badge className={`${PRIORITY_CLS[task.priority]} text-[10px]`}>{t(`task.${task.priority}`)}</Badge>
                      </div>
                    </div>
                    {task.description && <div className="text-xs text-muted-foreground line-clamp-2">{task.description}</div>}
                    {(task.checklist || []).length > 0 && (() => { const s = checklistStats(task); return (
                      <button type="button" onClick={() => setCommentTask({ task, tab: "checklist" })} className="w-full flex items-center gap-2 text-[10px] text-muted-foreground" data-testid={`task-checklist-bar-${task.id}`}>
                        <ListChecks className="h-3 w-3 shrink-0" /><Progress value={s.pct} className="h-1.5 flex-1" /><span className="font-mono">{s.done}/{s.total}</span>
                      </button>); })()}
                    {task.last_comment && (
                      <button type="button" onClick={() => setCommentTask({ task, tab: "comments" })} className="w-full text-left rounded bg-muted/60 px-2 py-1.5 text-[11px] text-muted-foreground hover:bg-muted transition-colors" data-testid={`task-last-comment-${task.id}`}>
                        <span className="font-medium text-foreground">{task.last_comment.author_name}:</span> <span className="line-clamp-2">{task.last_comment.text}</span>
                      </button>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <AssigneeAvatars ids={task.assignee_ids || []} users={users} />
                      {task.due_date && (
                        <span className={`flex items-center gap-1 text-[10px] font-mono ${overdue ? "text-red-500" : "text-muted-foreground"}`}>
                          <Calendar className="h-3 w-3" />{task.due_date}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-border">
                      <div className="flex gap-0.5">
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" disabled={col === "todo"} data-testid={`task-move-left-${task.id}`} onClick={() => move(task, -1)}>←</Button>
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs" disabled={col === "done"} data-testid={`task-move-right-${task.id}`} onClick={() => move(task, 1)}>→</Button>
                      </div>
                      <div className="flex gap-0.5 items-center">
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs gap-1" data-testid={`task-comments-${task.id}`} onClick={() => setCommentTask({ task, tab: "comments" })}>
                          <MessageSquare className="h-3 w-3" />{task.comment_count > 0 && <span>{task.comment_count}</span>}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-xs gap-1" data-testid={`task-attachments-${task.id}`} onClick={() => setCommentTask({ task, tab: "attachments" })}>
                          <Paperclip className="h-3 w-3" />{(task.attachments || []).length > 0 && <span>{task.attachments.length}</span>}
                        </Button>
                        {(() => { const run = (task.time_entries || []).find((e) => !e.end && e.user_id === user?.user_id); const hrs = (task.time_entries || []).filter((e) => e.end).reduce((s, e) => s + (e.seconds || 0), 0) / 3600; return (
                          <Button size="sm" variant="ghost" className={`h-6 px-1.5 text-xs gap-1 ${run ? "text-emerald-500 animate-pulse" : ""}`} title={run ? t("task.timerStop") : t("task.timerStart")} data-testid={`task-timer-${task.id}`}
                            onClick={async (e) => { e.stopPropagation(); try { await api.post(`/tasks/${task.id}/timer/${run ? "stop" : "start"}`); load(); } catch (err) { toast.error(err.response?.data?.detail || t("msg.error")); } }}>
                            {run ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}{hrs > 0 && <span className="font-mono">{fmtHours(hrs)}</span>}
                          </Button>); })()}
                        {!isViewer && (
                          <>
                            <Button size="icon" variant="ghost" className="h-6 w-6" title={t("tpl.saveAs")} data-testid={`task-save-template-${task.id}`} onClick={() => setSaveTpl(task)}>
                              <BookmarkPlus className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" data-testid={`task-edit-${task.id}`} onClick={() => setEditTask(task)}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" data-testid={`task-delete-${task.id}`} onClick={() => remove(task.id)}>
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {newOpen && <TaskDialog task={null} users={users} entities={entities} boardId={boardId} onClose={() => setNewOpen(false)} onSaved={load} />}
      {editTask && <TaskDialog task={editTask} users={users} entities={entities} boardId={boardId} onClose={() => setEditTask(null)} onSaved={load} />}
      {commentTask && <TaskDetailDialog task={tasks.find((x) => x.id === commentTask.task.id) || commentTask.task} initialTab={commentTask.tab} onClose={() => setCommentTask(null)} onChanged={load} />}
      {tplOpen && <TaskTemplatesDialog boardId={boardId} entityId={entity} onClose={() => setTplOpen(false)} onApplied={load} />}
      {saveTpl && <SaveTemplateDialog task={saveTpl} onClose={() => setSaveTpl(null)} />}
      {byClient && <ClientTasksDialog entityId={entity} onClose={() => setByClient(false)} onChanged={load} />}
    </div>
  );
}
